import { Injectable, inject, Injector, Type, ComponentRef } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

export interface FloatingComponentData {
  setData(data: Record<string, unknown>): void;
  close?(): void;
}

export interface FloatingComponentConfig {
  component: Type<FloatingComponentData>;
  data?: Record<string, unknown>;
  injector?: Injector;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  panelClass?: string | string[];
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  offsetX?: number;
  offsetY?: number;
}

@Injectable({
  providedIn: 'root',
})
export class FloatingComponentService {
  private readonly overlay = inject(Overlay);
  private currentOverlayRef: OverlayRef | null = null;
  private currentComponentRef: ComponentRef<FloatingComponentData> | null = null;
  private keydownSubscription: Subscription | null = null;

  open(config: FloatingComponentConfig): void {
    if (this.currentOverlayRef) {
      this.close();
    }

    const overlayConfig = this.createOverlayConfig(config);
    this.currentOverlayRef = this.overlay.create(overlayConfig);

    const componentPortal = new ComponentPortal(config.component, null, config.injector);
    this.currentComponentRef = this.currentOverlayRef.attach(componentPortal);

    if (config.data && this.currentComponentRef.instance) {
      const instance = this.currentComponentRef.instance;
      if (typeof instance.setData === 'function') {
        instance.setData(config.data);
      }
    }

    this.keydownSubscription = this.currentOverlayRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        this.triggerClose();
      }
    });
  }

  private triggerClose(): void {
    if (this.currentComponentRef) {
      const componentInstance = this.currentComponentRef.instance;

      if (componentInstance && typeof componentInstance.close === 'function') {
        componentInstance.close();
        return;
      }

      this.close();
    }
  }

  close(): void {
    this.currentComponentRef = null;

    if (this.keydownSubscription) {
      this.keydownSubscription.unsubscribe();
      this.keydownSubscription = null;
    }

    if (this.currentOverlayRef) {
      this.currentOverlayRef.dispose();
      this.currentOverlayRef = null;
    }
  }

  private createOverlayConfig(config: FloatingComponentConfig): OverlayConfig {
    const position = config.position || 'top-right';
    const offsetX = config.offsetX || 20;
    const offsetY = config.offsetY || 20;

    let positionStrategy;
    switch (position) {
      case 'top-right':
        positionStrategy = this.overlay.position().global().top(`${offsetY}px`).right(`${offsetX}px`);
        break;
      case 'top-left':
        positionStrategy = this.overlay.position().global().top(`${offsetY}px`).left(`${offsetX}px`);
        break;
      case 'bottom-right':
        positionStrategy = this.overlay.position().global().bottom(`${offsetY}px`).right(`${offsetX}px`);
        break;
      case 'bottom-left':
        positionStrategy = this.overlay.position().global().bottom(`${offsetY}px`).left(`${offsetX}px`);
        break;
      default:
        positionStrategy = this.overlay.position().global().top(`${offsetY}px`).right(`${offsetX}px`);
    }

    return new OverlayConfig({
      positionStrategy,
      hasBackdrop: false,
      panelClass: config.panelClass || 'floating-component-panel',
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      width: config.width,
      maxWidth: config.maxWidth || '320px',
      maxHeight: config.maxHeight || '90vh',
    });
  }
}
